"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";
import { IdleWarningModal } from "@/components/IdleWarningModal";
import { useAuth } from "@/hooks/useAuth";
import { useIdleLogout } from "@/hooks/useIdleLogout";

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-black">
      <p className="text-zinc-600 dark:text-zinc-400">Loading...</p>
    </div>
  );
}

function AuthenticatedLayoutInner({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isLoading, isAuthenticated, isUnauthenticated } = useAuth();
  const { showWarning, secondsLeft, staySignedIn, signOutNow } =
    useIdleLogout(isAuthenticated);

  useEffect(() => {
    if (isUnauthenticated) {
      const isManualSignOut = sessionStorage.getItem("manualSignOut");
      if (isManualSignOut) {
        sessionStorage.removeItem("manualSignOut");
        router.replace("/unauthenticated");
        return;
      }
      const qs = searchParams.toString();
      const currentUrl = pathname + (qs ? `?${qs}` : "");
      const redirectUrl =
        currentUrl !== "/"
          ? `/unauthenticated?returnUrl=${encodeURIComponent(currentUrl)}`
          : "/unauthenticated";
      router.replace(redirectUrl);
    }
  }, [isUnauthenticated, router, pathname, searchParams]);

  if (isLoading || isUnauthenticated) {
    return <LoadingScreen />;
  }

  return (
    <>
      <div className="min-h-screen bg-gray-100 pl-16 pt-14">{children}</div>
      <IdleWarningModal
        open={showWarning}
        secondsLeft={secondsLeft}
        onStay={staySignedIn}
        onSignOut={signOutNow}
      />
    </>
  );
}

export default function AuthenticatedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <AuthenticatedLayoutInner>{children}</AuthenticatedLayoutInner>
    </Suspense>
  );
}
